import { Injectable } from '@angular/core';
import jwtDecode from 'jwt-decode';
import { ILoggedInUser, IUserModel } from '../../models/auth/user.model';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { IToken } from '../../models/auth/token.model';
import { HttpService } from '../shared/http-serice.service';
import { MessageService } from 'primeng/api';
import { Permission } from '../../enums/permissions/Permission';
import { DecodedErpToken } from 'src/app/models/auth/erptoken.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  loginUrl = 'auth/login';
  generateTokenUrl = 'auth/generate-token';
  public accessToken$: Observable<string | null>;
  private accessTokenSubject: BehaviorSubject<string | null>;
  private refreshTokenTimeout?: any;

  constructor(
    private toastService: MessageService,
    private http: HttpService,
    private router: Router
  ) {
    // Initialize BehaviorSubject with null or your initial token value
    this.accessTokenSubject = new BehaviorSubject<string | null>(
      this.getToken()
    );
    this.accessToken$ = this.accessTokenSubject.asObservable();
  }

  setToken(token: string, account = {}, refreshToken = ''): void {
    localStorage.setItem('token', token);
    localStorage.setItem('refresh-token', refreshToken);
    localStorage.setItem('account', JSON.stringify(account));
    const jwtToken: IToken | null = this.decodeToken();
    if (jwtToken) {
      localStorage.setItem('exp', jwtToken.exp.toString());
    }
    let perms: string[] = [];
    perms.push(
      ...account['aclPermissions'].map((permission: any) => permission['slug'])
    );
    perms = perms.join(',').split(',');
    localStorage.setItem('aclPermissions', JSON.stringify(perms));
  }

  updateToken(token: string, refreshToken: string): void {
    localStorage.setItem('token', token);
    localStorage.setItem('refresh-token', refreshToken);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  decodeToken(): IToken | null {
    const undecodedToken: string | null = this.accessTokenSubject.value;
    return jwtDecode(undecodedToken!);
  }

  isLoggedIn() {
    return !!this.accessTokenSubject.value;
  }

  getLoggedInUser(): ILoggedInUser | null {
    const jsonString = localStorage.getItem('account');
    if (jsonString) {
      try {
        const loggedInUserObject = JSON.parse(jsonString);

        return loggedInUserObject as ILoggedInUser;
      } catch (error) {}
    }
    return null;
  }

  logout() {
    this.http.post<any>(`auth/revoke-token`, {}).subscribe(() => {});
    this.accessTokenSubject.next(null);
    this.stopRefreshTokenTimer();

    localStorage.clear();
    this.deleteErpCookie(this.cookieKey);
    //return to erp
    window.location.href = environment.erpUiUrl;
    //  this.router.navigate([''])
  }

  login(userCredentials: IUserModel): Observable<any> {
    return this.http.post<any>(this.loginUrl, userCredentials).pipe(
      tap((response) => {
        this.accessTokenSubject.next(response.value.token);
        this.startRefreshTokenTimer();
        return response;
      })
    );
  }

  generateToken(token: string): Observable<any> {
    let body = null;
    return this.http.post<any>(`${this.generateTokenUrl}?token=${token}`, {}).pipe(
      tap((response) => {
        this.accessTokenSubject.next(response.value.token);
        this.startRefreshTokenTimer();
        return response;
      })
    );
  }

  refreshToken() {
    return this.http.post<any>(`auth/refresh-token`, {}).pipe(
      tap((response) => {
        this.accessTokenSubject.next(response.value.token);
        this.updateToken(response.value.token, response.value.refreshToken);
        this.startRefreshTokenTimer();
        return response;
      })
    );
  }

  getAccessToken(): string | null {
    return this.accessTokenSubject.value;
  }

  getRefreshToken() {
    return localStorage.getItem('refresh-token');
  }

  private startRefreshTokenTimer() {
    const jwtToken = this.decodeToken();

    if (!jwtToken) {
      return;
    }

    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - 60 * 1000;
    this.refreshTokenTimeout = setTimeout(
      () => this.refreshToken().subscribe(),
      timeout
    );
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }
  redirectOnPermission(permissions: any[], router: any) {
    let permissionToRouteMapping = {
      [Permission.VIEW_NAVBAR_DASHBOARD_MENU]: '/dashboard',
      [Permission.VIEW_NAVBAR_PROJECTS_MENU]: '/projects',
      [Permission.VIEW_NAVBAR_CONTRACTS_MENU]: '/contracts',
      [Permission.VIEW_NAVBAR_RESOURCES_MENU]: '/resources',
      [Permission.VIEW_NAVBAR_REPORTS_MENU]: '/reports',
      [Permission.VIEW_NAVBAR_SETTINGS_MENU]: '/settings',
    };
    for (const permission of permissions) {
      const route = permissionToRouteMapping[permission['slug']];
      if (route) {
        router.navigate([route]);
        break;
      }
    }
  }

  get cookieKey() {
    return (
      'ERPToken' +
      (environment.production
        ? ''
        : environment.staging
        ? '-staging'
        : '-development')
    );
  }

  get hostName() {
    return location.hostname.split('.').length == 1
      ? location.hostname
      : location.hostname
          .split('.')
          .splice(location.hostname.split('.').length - 2, 2)
          .join('.');
  }

  getErpCookie(): string | null {
    const nameEQ = `${this.cookieKey}=`;
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ')
        cookie = cookie.substring(1, cookie.length);
      if (cookie.indexOf(nameEQ) === 0)
        return cookie.substring(nameEQ.length, cookie.length);
    }
    return null;
  }

  getErpTokenFromCookie(): string | null {
    const cookie = this.getErpCookie();
    return cookie ? JSON.parse(cookie).token : null;
  }

  decodeErpToken(token: string): DecodedErpToken | null {
    if (!token) return null;
    return jwtDecode(token);
  }

  deleteErpCookie(name: string) {
    const domain = `domain=.${this.hostName}`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;${domain};path=/`;
  }
}
